// LIBRERIE/IMPORTS
import React from "react";
import { useTranslation } from "react-i18next";

// CONFIGURAZIONI E UTILS
import propertyFieldsConfig from "../../config/propertyFields.json";
import {
    getContractTranslation,
    getPropertyStatusTranslation,
    getHeatingTypeTranslation,
    getHeatingFuelNameTranslation,
    getFurnitureTranslation,
    getFloorTranslation,
    LanguageCode
} from '../../config/i18n/elementsMappingProperty';

interface PropertyDetailsProps {
    propertyDetails: any;
    isResidential: boolean;
    isPlot: boolean;
    isGarage: boolean;
    isValidValue: (value: any) => boolean;
    getNestedValue: (obj: any, path: string) => any;
    decodeHTMLEntities: (text: string) => string;
    language: string;
}

// Funzione formatMonthlyExpenses
const formatMonthlyExpenses = (expenses: string | number, locale: string = 'it-IT'): string => {

    const formatConfig: Intl.NumberFormatOptions = {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    };

    if (typeof expenses === 'number') {
        return new Intl.NumberFormat(locale, formatConfig).format(expenses);
    }
    if (typeof expenses === 'string') {
        if (/^\d+$/.test(expenses)) {
            return new Intl.NumberFormat(locale, formatConfig).format(parseInt(expenses, 10));
        }
        if (/^\d+,\d{2}$/.test(expenses)) {
            const parsed = parseFloat(expenses.replace(',', '.'));
            return new Intl.NumberFormat(locale, formatConfig).format(parsed);
        }
        if (/^\d+\.\d{2}$/.test(expenses)) {
            return new Intl.NumberFormat(locale, formatConfig).format(parseFloat(expenses));
        }
    }
    return String(expenses);
};


const PropertyDetails: React.FC<PropertyDetailsProps> = ({
    propertyDetails,
    isResidential,
    isGarage,
    isPlot,
    isValidValue,
    getNestedValue,
    language
}) => {

    const { t, i18n } = useTranslation();
    const currentLocaleForFormatting = i18n.language;
    const yesNo = (value: boolean | undefined | null) => value ? t('common.yes') : t('common.no');
    const negotiableText = t('common.negotiable');

    // Dettagli terrazzo (m2 e abitabilità)
    const terraceDetailsList = propertyDetails.residential?.terraceDetails?.terracesCharacteristics ? JSON.parse(propertyDetails.residential.terraceDetails.terracesCharacteristics) : null;

    let terraceM2List = [];
    let terraceHabitabilityList = [];

    if (terraceDetailsList != null) {
        for (const key in terraceDetailsList) {
            if (key.includes("tipo_terrazzo") && (terraceDetailsList[key] != "" && terraceDetailsList[key] != 0)) {
                terraceHabitabilityList.push({ [key]: terraceDetailsList[key] })
            } else if (key.startsWith("terrazzo") && (terraceDetailsList[key] != "" && terraceDetailsList[key] != 0)) {
                terraceM2List.push({ [key]: terraceDetailsList[key] })
            }
        }
    }

    // Dettaglio balconi (m2)
    const balconyDetailsList = propertyDetails.residential?.balconyCharacteristics ? JSON.parse(propertyDetails.residential.balconyCharacteristics) : null;

    let balconyM2List = [];

    if (balconyDetailsList != null) {
        for (const key in balconyDetailsList) {
            if (key.startsWith("balcone") && (balconyDetailsList[key] != "" && balconyDetailsList[key] != 0)) {
                balconyM2List.push({ [key]: balconyDetailsList[key] })
            }
        }
    }

    return (
        <div className="property-list mb-0">

            <ul className="list-unstyled row mb-0 px-3">
                {propertyFieldsConfig.rif && isValidValue(propertyDetails.ref) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.reference')}:</b> {propertyDetails.ref}
                    </li>
                )}

                {propertyFieldsConfig.price && isValidValue(propertyDetails.price) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('common.price')}:</b>
                        {propertyDetails.reservedNegotiation
                            ? t('property.priceReserved')
                            : propertyDetails.auctionDetails.auction
                                ? `da € ${new Intl.NumberFormat("it-IT").format(propertyDetails.price)}`
                                : `€ ${new Intl.NumberFormat("it-IT").format(propertyDetails.price)}${propertyDetails.negotiable ? ` ${negotiableText}` : ""}`
                        }
                    </li>
                )}

                {propertyFieldsConfig.sqmtCommercial && isValidValue(propertyDetails.sqmtCommercial) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.commercialArea')}:</b> {propertyDetails.sqmtCommercial} m²
                    </li>
                )}

                {propertyFieldsConfig.sqmt && isValidValue(propertyDetails.sqmt) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.area')}:</b> {propertyDetails.sqmt} m²
                    </li>
                )}

                {isResidential && propertyFieldsConfig["floorDetails.floor"] && isValidValue(getNestedValue(propertyDetails, "floorDetails.floorId")) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.floor')}:</b>
                        {getFloorTranslation(getNestedValue(propertyDetails, "floorDetails.floorId"), language as LanguageCode)}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.floors && isValidValue(propertyDetails.floors) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.totalFloors')}:</b> {propertyDetails.floors}
                    </li>
                )}
                {propertyDetails.elevator && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.elevator')}:</b> {yesNo(propertyDetails.elevator)}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.independentEntry && isValidValue(propertyDetails.residential?.entryDetails.indipendentEntry) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.entry.independent')}:</b> {yesNo(propertyDetails.residential?.entryDetails.indipendentEntry)}
                    </li>
                )}


                {propertyFieldsConfig["propertyStatusDetails.propertyStatus"] && isValidValue(getNestedValue(propertyDetails, "propertyStatusDetails.propertyStatusId")) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.status')}: </b>
                        {getPropertyStatusTranslation(getNestedValue(propertyDetails, "propertyStatusDetails.propertyStatusId"), language as LanguageCode)}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.premises && isValidValue(propertyDetails.premises) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.roomsCount')}:</b> {propertyDetails.premises}
                    </li>
                )}

                {/* Soggiorno se ha i m2 */}
                {(['livingRoom', 'hall', 'diningRoom'] as const).map(roomType => {
                    const detailsPath = `residential.${roomType}Details.${roomType}`;
                    const sqmtPath = `residential.${roomType}Details.${roomType}Sqmt`;
                    // type ResidentialConfig = typeof propertyFieldsConfig.residential;
                    let isFieldEnabled = true;
                    switch (roomType) {
                        case 'livingRoom':
                            isFieldEnabled = !!(propertyFieldsConfig.residential.livingRoomDetails?.livingRoom);
                            break;
                        case 'hall':
                            isFieldEnabled = !!(propertyFieldsConfig.residential.hallDetails?.hall);
                            break;
                        case 'diningRoom':
                            isFieldEnabled = !!(propertyFieldsConfig.residential.diningRoomDetails?.diningRoom);
                            break;
                    }
                    return isResidential && isFieldEnabled && isValidValue(getNestedValue(propertyDetails, detailsPath)) && (
                        <li className="mb-2 col-sm-6" key={roomType}>
                            <b>{t(`property.${roomType}`)}:</b>
                            {getNestedValue(propertyDetails, detailsPath) && getNestedValue(propertyDetails, sqmtPath) > 0
                                ? ` ${yesNo(getNestedValue(propertyDetails, detailsPath))} (${getNestedValue(propertyDetails, sqmtPath)} m²)`
                                : yesNo(getNestedValue(propertyDetails, detailsPath))}
                        </li>
                    );
                })}

                {isResidential && !isGarage && propertyFieldsConfig.residential["kitchenDetails.kitchenName"] && isValidValue(propertyDetails.residential?.kitchenDetails.kitchenName) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.kitchen')}:</b> {propertyDetails.residential?.kitchenDetails.kitchenName}
                    </li>
                )}

                {/* Camere */}
                {isResidential && !isGarage && propertyFieldsConfig.residential.rooms && isValidValue(propertyDetails.residential?.rooms) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.bedrooms.total')}:</b> {propertyDetails.residential?.rooms}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.kingSizeBedRooms && isValidValue(propertyDetails.residential?.kingSizeBedRooms) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.bedrooms.kingSize')}:</b> {propertyDetails.residential?.kingSizeBedRooms}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.doubleBedRooms && isValidValue(propertyDetails.residential?.doubleBedRooms) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.bedrooms.double')}:</b> {propertyDetails.residential?.doubleBedRooms}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.singleBedRooms && isValidValue(propertyDetails.residential?.singleBedRooms) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.bedrooms.single')}:</b> {propertyDetails.residential?.singleBedRooms}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.bathrooms && isValidValue(propertyDetails.residential?.bathrooms) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.bathroomsCount')}:</b> {propertyDetails.residential?.bathrooms}
                    </li>
                )}
                {/* Spese Mensili */}
                {propertyFieldsConfig.monthlyExpenses && isValidValue(propertyDetails.monthlyExpenses) && (propertyDetails.monthlyExpenses !== '0') && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.monthlyExpenses')}:</b> {formatMonthlyExpenses(propertyDetails.monthlyExpenses, currentLocaleForFormatting)}
                    </li>
                )}


                {/* Terrazzi */}
                {propertyFieldsConfig.residential.terraceDetails.terrace && isValidValue(propertyDetails.residential?.terraceDetails.terrace) && (
                    <li className="mb-2 col-sm-6">
                        <ul className="pl-0 list-unstyled">
                            {terraceM2List.map((terrace, index) => {
                                return (
                                    <li key={index}>
                                        <span className="pr-2"><b>{t('property.terrace') + ' ' + (index + 1)} :</b></span>
                                        <span>{Object.values(terrace)} m²</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </li>
                )}

                {/* Balconi  */}
                {propertyFieldsConfig.residential.balconyCharacteristics && isValidValue(propertyDetails.residential?.balcony) && (                    
                    <li className="mb-2 col-sm-6">
                        <ul className="pl-0 list-unstyled">
                            {balconyM2List.map((balcony, index) => {
                                return (
                                    <li key={index}>
                                        <span className="pr-2">{t('property.balcony') + ' ' + (index + 1)} :</span>
                                        <span>{Object.values(balcony)} m²</span>
                                    </li>
                                );
                            })}
                        </ul>
                    </li>
                )}

                {/* Posti Auto */}
                {isResidential && propertyFieldsConfig.residential.carPlaces && propertyDetails.residential?.carPlaces !== '0' && isValidValue(propertyDetails.residential?.carPlaces) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.parking.space')}:</b> {propertyDetails.residential?.carPlaces}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.carBoxDetails.carBoxName && propertyDetails.residential?.carPlaces !== '0' && isValidValue(getNestedValue(propertyDetails, "residential.carBoxDetails.carBoxName")) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.parking.type')}:</b> {getNestedValue(propertyDetails, "residential.carBoxDetails.carBoxName")}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.unclampedCarPlaces && isValidValue(propertyDetails.residential?.unclampedCarPlaces) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.parking.outdoorSpaces')}:</b> {propertyDetails.residential.unclampedCarPlaces}
                    </li>
                )}

                {/* Garage */}
                {isResidential && propertyFieldsConfig.residential.garageDetails.garage1 && isValidValue(propertyDetails.residential?.garageDetails.garage1) && propertyFieldsConfig.residential.garageDetails.garage2 && isValidValue(propertyDetails.residential?.garageDetails.garage2) ? (
                    <>
                        <li className="mb-2 col-sm-6">
                            <b>{t('property.parking.garage1')}:</b> {yesNo(propertyDetails.residential?.garageDetails.garage1)} {propertyDetails.residential?.garageDetails.garage1 && propertyDetails.residential?.garageDetails.garage1Sqmt > 0 ? `(${propertyDetails.residential?.garageDetails.garage1Sqmt} m²)` : ''}
                        </li>
                        <li className="mb-2 col-sm-6">
                            <b>{t('property.parking.garage2')}:</b> {yesNo(propertyDetails.residential?.garageDetails.garage2)} {propertyDetails.residential?.garageDetails.garage2 && propertyDetails.residential?.garageDetails.garage2Sqmt > 0 ? `(${propertyDetails.residential?.garageDetails.garage2Sqmt} m²)` : ''}
                        </li>
                    </>
                ) : isResidential && propertyFieldsConfig.residential.garageDetails.garage1 && isValidValue(propertyDetails.residential?.garageDetails.garage1) ? (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.parking.garage')}:</b> {yesNo(propertyDetails.residential?.garageDetails.garage1)} {propertyDetails.residential?.garageDetails.garage1 && propertyDetails.residential?.garageDetails.garage1Sqmt > 0 ? `(${propertyDetails.residential?.garageDetails.garage1Sqmt} m²)` : ''}
                    </li>
                ) : isResidential && propertyFieldsConfig.residential.garageDetails.garage2 && isValidValue(propertyDetails.residential?.garageDetails.garage2) ? (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.parking.garage')}:</b> {yesNo(propertyDetails.residential?.garageDetails.garage2)} {propertyDetails.residential?.garageDetails.garage2 && propertyDetails.residential?.garageDetails.garage2Sqmt > 0 ? `(${propertyDetails.residential?.garageDetails.garage2Sqmt} m²)` : ''}
                    </li>
                ) : null}

                {/* Cantina */}
                {isResidential && propertyFieldsConfig.residential.cellarDetails.cellar && isValidValue(propertyDetails.residential?.cellarDetails.cellar) ? (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.cellar')}:</b> {yesNo(propertyDetails.residential?.cellarDetails.cellar)} {propertyDetails.residential?.cellarDetails.cellar && propertyDetails.residential?.cellarDetails.cellarSqmt > 0 ? `(${propertyDetails.residential?.cellarDetails.cellarSqmt} m²)` : ''}
                    </li>
                ) : null}

                {/* Giardino, Giardino Privato, Giardino Condominiale, Resede, Terreno, Altro Terreno */}
                {['garden', 'privateGarden', 'condominiumGarden', 'resede', 'land', 'otherLand'].map(field => {
                    const detailsPath = `residential.gardenDetails.${field}`;

                    let labelKey = '';
                    switch (field) {
                        case 'garden':
                            labelKey = 'property.garden.label';
                            break;
                        case 'privateGarden':
                            labelKey = 'property.garden.privateGarden';
                            break;
                        case 'condominiumGarden':
                            labelKey = 'property.garden.condominiumGarden';
                            break;
                        case 'resede':
                            labelKey = 'property.courtyard';
                            break;
                        case 'land':
                            labelKey = 'property.land';
                            break;
                        case 'otherLand':
                            labelKey = 'property.otherLand';
                            break;
                        default:
                            labelKey = `property.${field}`;
                    }

                    return  (
                        isResidential && 
                        propertyFieldsConfig.residential.gardenDetails?.[field as keyof typeof propertyFieldsConfig.residential.gardenDetails] && 
                        isValidValue(getNestedValue(propertyDetails, detailsPath)) && (
                            <li className="mb-2 col-sm-6" key={field}>
                                <b>{t(labelKey)}:</b>
                                {yesNo(getNestedValue(propertyDetails, detailsPath))}
                                {(getNestedValue(propertyDetails, detailsPath)) && 
                                propertyDetails.residential?.gardenDetails?.[`${field}Sqmt` as keyof typeof propertyDetails.residential.gardenDetails] > 0
                                    ? ` (${propertyDetails.residential?.gardenDetails?.[`${field}Sqmt` as keyof typeof propertyDetails.residential.gardenDetails]} m²)`
                                    : ''}
                            </li>
                        )
                    )
                })}

                {isResidential && propertyFieldsConfig.residential.tavernDetails.tavern && isValidValue(propertyDetails.residential?.tavernDetails.tavern) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.tavern')}:</b> {yesNo(propertyDetails.residential.tavernDetails.tavern)}
                        {propertyDetails.residential.tavernDetails.tavern && propertyDetails.residential.tavernDetails.tavernSqmt > 0
                            ? ` (${propertyDetails.residential.tavernDetails.tavernSqmt} m²)`
                            : ''}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.mansardDetails.mansard && isValidValue(propertyDetails.residential?.mansardDetails.mansard) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.attic')}:</b> {yesNo(propertyDetails.residential.mansardDetails.mansard)}
                        {propertyDetails.residential.mansardDetails.mansard && propertyDetails.residential.mansardDetails.mansardSqmt > 0
                            ? `, ${propertyDetails.residential.mansardDetails.mansardSqmt} m²`
                            : ''}
                    </li>
                )}

                {/* Riscaldamento */}
                {isResidential && propertyFieldsConfig.residential.heatingDetails.heatingName && isValidValue(getNestedValue(propertyDetails, "residential.heatingDetails.heatingId")) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.heating.label')}:</b> {getHeatingTypeTranslation(getNestedValue(propertyDetails, "residential.heatingDetails.heatingId"), language as LanguageCode)}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.heatingDetails.heatingTypeName && isValidValue(getNestedValue(propertyDetails, "residential.heatingDetails.heatingTypeId")) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.heating.type')}:</b> {getHeatingTypeTranslation(getNestedValue(propertyDetails, "residential.heatingDetails.heatingTypeId"), language as LanguageCode)}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.heatingDetails.heatingFuelName && isValidValue(getNestedValue(propertyDetails, "residential.heatingDetails.heatingFuelId")) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.heating.fuel')}:</b> {getHeatingFuelNameTranslation(getNestedValue(propertyDetails, "residential.heatingDetails.heatingFuelId"), language as LanguageCode)}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.conditionerDetails.conditioner && isValidValue(propertyDetails.residential?.conditionerDetails.conditioner) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.airConditioning')}:</b> {yesNo(propertyDetails.residential.conditionerDetails.conditioner)}
                    </li>
                )}

                {propertyFieldsConfig.enerClassDetails.name && isValidValue(propertyDetails.enerClassDetails?.enerClassName) && (
                    <li className="mb-2 col-sm-6">
                        <b>{t('property.energyClass.label')}:</b> {t(`property.energyClass.status.${propertyDetails.enerClassDetails.enerClassName}`, { defaultValue: propertyDetails.enerClassDetails.enerClassName })}
                    </li>
                )}

                {/* IPE */}
                {propertyDetails.apeDetails?.apeAfterOct2015 ? (
                    <>
                        {propertyFieldsConfig.apeDetails.apeAfterOct2015 && isValidValue(propertyDetails.ipe) && ( // IPE è EPgl,nren
                            <li className="mb-2 col-sm-6">
                                <b>{t('property.energyClass.epGlNren')}:</b> {Number(propertyDetails.ipe).toFixed(2)} {t('property.energyClass.unit')}
                            </li>
                        )}
                        {propertyFieldsConfig.apeDetails.apeRin && isValidValue(propertyDetails.apeDetails?.apeRin) && (
                            <li className="mb-2 col-sm-6">
                                <b>{t('property.energyClass.epGlRen')}:</b> {Number(propertyDetails.apeDetails.apeRin).toFixed(2)} {t('property.energyClass.unit')}
                            </li>
                        )}
                    </>
                ) : (
                    propertyFieldsConfig.ipe && isValidValue(propertyDetails.ipe) && (
                        <li className="mb-2 col-sm-6">
                            <b>{t('property.energyClass.IPE')}:</b> {Number(propertyDetails.ipe).toFixed(2)} {t('property.energyClass.unit')}
                        </li>
                    )
                )}

                {/* -------------- Non richieste -------------------- */}

                {propertyFieldsConfig.auction && propertyDetails.auctionDetails.auction && isValidValue(propertyDetails.auctionDetails.date) && (
                    <li className="col-sm-6 col-xl-4 mb-2">
                        <b>{t('property.auctionDate')}:</b> {new Date(propertyDetails.auctionDetails.date * 1000).toLocaleDateString(i18n.language)}
                    </li>
                )}

                {propertyFieldsConfig["contractDetails.contractName"] && isValidValue(getNestedValue(propertyDetails, "contractDetails.contractId")) && (
                    <li className="col-sm-6 col-xl-4 mb-2">
                        <b>{t('property.contract')}:</b>
                        {getContractTranslation(getNestedValue(propertyDetails, "contractDetails.contractId"), language as LanguageCode)}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.traditionalEntry && isValidValue(propertyDetails.residential?.entryDetails.traditionalEntry) && (
                    <li className="col-sm-6 col-xl-4 mb-2">
                        <b>{t('property.entry.traditional')}:</b> {yesNo(propertyDetails.residential?.entryDetails.traditionalEntry)}
                    </li>
                )}

                {isResidential && propertyFieldsConfig.residential.lastFloor && isValidValue(propertyDetails.residential?.lastFloor) && (
                    <li className="col-sm-6 col-xl-4 mb-2">
                        <b>{t('property.lastFloor')}:</b> {yesNo(propertyDetails.residential?.lastFloor)}
                    </li>
                )}

                {/* Arredamento - Se ha ID 2 o 0 non mostarre (nessuno - non arredato)*/}
                {isResidential && propertyFieldsConfig.residential.furnitureDetails.furnitureName && ['0', '2'].includes(propertyDetails.residential?.furnitureDetails.furnitureId) && isValidValue(getNestedValue(propertyDetails, "residential.furnitureDetails.furnitureId")) && (
                    <li className="col-sm-6 col-xl-4 mb-2">
                        <b>{t('property.furnished')}:</b>
                        {getFurnitureTranslation(getNestedValue(propertyDetails, "residential.furnitureDetails.furnitureId"), language as LanguageCode)}
                    </li>
                )}

                {['utilityRoom', 'studio'].map(roomType => {
                    let detailsPath = `residential.${roomType}`;
                    let sqmtPath = `residential.${roomType}Sqmt`;
                    if (roomType === 'studio') {
                        detailsPath = `residential.studioDetails.studio`;
                        sqmtPath = `residential.studioDetails.studioSqmt`;
                    }
                    const roomKey = `property.${roomType}`;
                    const configFieldKey = roomType === 'studio' ? 'studioDetails.studio' : roomType;
                    const isFieldEnabled = propertyFieldsConfig.residential[configFieldKey as keyof typeof propertyFieldsConfig.residential];
                    return isResidential &&
                        isFieldEnabled &&
                        isValidValue(getNestedValue(propertyDetails, detailsPath)) && (
                            <li className="col-sm-6 col-xl-4 mb-2" key={roomType}>
                                <b>{t(roomKey)}:</b> {yesNo(getNestedValue(propertyDetails, detailsPath))}
                                {getNestedValue(propertyDetails, detailsPath) && getNestedValue(propertyDetails, sqmtPath) > 0
                                    ? `, ${getNestedValue(propertyDetails, sqmtPath)} m²`
                                    : ''}
                            </li>
                        );
                })}

                {/* Prestazioni Fabbricato */}
                {propertyFieldsConfig.performanceDetails.winter && isValidValue(propertyDetails.performanceDetails?.winter) && propertyDetails.performanceDetails?.winter !== "0" && (
                    <li className="col-sm-6 col-xl-4 mb-2">
                        <b>{t('property.performance.winter')}:</b> {t(`property.energyClass.levels.${propertyDetails.performanceDetails.winter.toLowerCase()}`, { defaultValue: propertyDetails.performanceDetails.winter })}
                    </li>
                )}

                {propertyFieldsConfig.performanceDetails.summer && isValidValue(propertyDetails.performanceDetails?.summer) && propertyDetails.performanceDetails?.summer !== "0" && (
                    <li className="col-sm-6 col-xl-4 mb-2">
                        <b>{t('property.performance.summer')}:</b> {t(`property.energyClass.levels.${propertyDetails.performanceDetails.summer.toLowerCase()}`, { defaultValue: propertyDetails.performanceDetails.summer })}
                    </li>
                )}

                {/* --- PLOT FIELDS (Terreni) --- */}
                {isPlot && Object.entries(propertyFieldsConfig.serviceDetails).map(([key, isEnabled]) => {
                    const utilityKey = `property.utilities.${key.replace('Available', '').replace('Availability', '').replace('System', '').replace('Access', '').replace('Line', '').replace('Lightning', '').toLowerCase()}`;
                    const valuePath = `serviceDetails.${key}`;
                    const finalUtilityKey = key === 'publicLightning' ? 'property.utilities.publicLighting' : utilityKey;

                    return isEnabled && isValidValue(getNestedValue(propertyDetails, valuePath)) && (
                        <li className="col-sm-6 col-xl-4 mb-2" key={key}>
                            <b>{t(finalUtilityKey)}:</b> {yesNo(getNestedValue(propertyDetails, valuePath))}
                        </li>
                    );
                })}
            </ul>
        </div>
    );
};

export default PropertyDetails;